// Globals
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';

// Store
import { useAppDispatch, useAppState } from '@/store';

// Components
import { StyledContentBlock, StyledHeaderBlock, StyledPageTitle, StyledPageWrapper } from './GenerateQrContainerStyles';
import { CreateQr, GeneratedQrList } from '../../components';
import { Modal, ModalSize, showSuccessToast, useForm } from '@/components';
import { Button } from '@/ui';

// Models
import { IGeneratedQrItem } from '@/models/generateQr.model';

// Modules
import { getProductsList, productsSelector } from '@/modules/Products';
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { useModal, usePagination } from '@/hooks';
import { createProductsQrList, generatedQrSelector, getGeneratedQrList } from '../../feature';
import { TGenerateQrFormValues } from '../../helpers';
import { CreateProductQrDTO } from '../../dtos';

const GenerateQrContainer = () => {
  const { t } = useTranslation(['products']);
  const dispatch = useAppDispatch();
  const { isLoading, productsQrList } = useAppState(generatedQrSelector);
  const { productsList } = useAppState(productsSelector);
  const [form] = useForm();
  const { isOpened, openModal, closeModal } = useModal();
  const { currentPage, onPageChange } = usePagination();
  const { canManage } = usePermissions();

  const canManageQr = canManage(Resource.QR_GENERATION);

  const handleSubmit = useCallback(async (values: TGenerateQrFormValues) => {
    const dto = new CreateProductQrDTO(values);
    try {
      await dispatch(createProductsQrList(dto)).unwrap();
      dispatch(getGeneratedQrList({ page: 1 }));
      closeModal();
      form.resetFields();
      showSuccessToast({ message: t('data_saved') });
    } catch {}
  }, []);

  const debouncedFetchProducts = useCallback(
    debounce((searchText: string) => {
      dispatch(getProductsList({ search: searchText }));
    }, 1000),
    [],
  );

  useEffect(() => {
    dispatch(getGeneratedQrList({ page: currentPage }));
  }, [currentPage]);

  useEffect(() => {
    dispatch(getProductsList({ name: '' }));
  }, []);

  return (
    <StyledPageWrapper>
      <StyledHeaderBlock>
        <StyledPageTitle>{t('generate_qr_title')}</StyledPageTitle>
        {canManageQr && <Button onClick={openModal}>{t('generate_qr_create_title')}</Button>}
      </StyledHeaderBlock>
      <StyledContentBlock>
        <GeneratedQrList
          qrList={productsQrList?.data as IGeneratedQrItem[]}
          isLoading={isLoading}
          currentPage={currentPage}
          onPageChange={onPageChange}
          total={productsQrList?.meta.total ?? 0}
        />
      </StyledContentBlock>

      <Modal title={t('generate_qr_create_title')} visible={isOpened} onCancel={closeModal} size={ModalSize.LARGE}>
        <CreateQr
          formInstance={form}
          productsList={productsList?.data}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          debouncedFetchProducts={debouncedFetchProducts}
        />
      </Modal>
    </StyledPageWrapper>
  );
};

export default GenerateQrContainer;
